import { Alert } from '@/shared/components/rebranded/Alert/Alert';
import Input from '@/shared/components/rebranded/Input/Input';
import LoadingIndicator from '@/shared/components/rebranded/LoadingIndicator/LoadingIndicator';
import Modal from '@storeblocks/modal';
import { FormEvent, useRef, useState } from 'react';
import { useCacheController } from './CacheProvider';
import { getCustomer } from './api/customer';
import { clearState, loadState, saveState } from './persistence';
import Paper from './shared/components/Paper/Paper';
import Button from './shared/components/rebranded/Button/Button';
import { useAbortablePromise } from './shared/hooks';
import auth, { isAdvisor } from './utils/auth';

export const ADVISOR_MODE_SESSION_STORAGE_KEY = 'advisorMode';

export function startAdvisorSession(cmId: string) {
  saveState('advisorMode', { cmId });
}

export function readAdvisorSession() {
  return loadState('advisorMode');
}

function quitAdvisorSession() {
  clearState('advisorMode');
}

export function LoggedInAsAdvisor() {
  const cacheController = useCacheController();

  const [{ data: customerData, loading }] = useAbortablePromise(
    async signal => {
      if (!isAdvisor()) {
        return null;
      }

      return cacheController.read('customer/core-data', () =>
        getCustomer({ signal })
      );
    },
    []
  );

  const handleQuit = () => {
    quitAdvisorSession();
    cacheController.clear();
    window.location.pathname = '/meld-skade/radgiver';
  };

  if (!auth.authenticated) {
    auth.login();
    return null;
  }

  if (!isAdvisor()) {
    return null;
  }

  if (!auth.authenticatedOnBehalfOf) {
    window.location.pathname = '/meld-skade/radgiver';
  }

  if (!loading && customerData == null) {
    return (
      <Modal
        heading="Feil"
        onClose={handleQuit}
        open
        content={
          <Alert
            variant="error"
            title={`Fant ingen bruker med cmId ${auth.authenticatedOnBehalfOf}`}
            description="Sjekk om ID-en er riktig og prøv igjen."
            fluid
            style={{ marginTop: 16 }}
          />
        }
        footer={
          <Button onClick={handleQuit} style={{ marginTop: 16 }}>
            Velg ny kunde
          </Button>
        }
        trigger={undefined}
      />
    );
  }

  return (
    <div
      style={{
        background: '#282828',
        color: '#fff',
        top: 0,
        position: 'absolute',
        zIndex: 10000000,
        right: 0,
        left: 0,
        height: 70,
        padding: '0 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <div>
        {auth.idTokenParsed.given_name} {auth.idTokenParsed.family_name} melder
        på vegne av{' '}
        {loading ? (
          <LoadingIndicator />
        ) : (
          <strong>
            {customerData?.name ?? auth.advisor.on_behalf_of?.cm_id}
          </strong>
        )}
      </div>
      <Button onClick={handleQuit}>Avslutt</Button>
    </div>
  );
}

type AdvisorLoginFormProps = {
  onSubmit: ({
    cmId,
    claimId,
    printToPDF,
    isCorporate
  }: {
    cmId: string;
    claimId: string | null;
    printToPDF: boolean;
    isCorporate: boolean;
  }) => void;
};

function extractQueryParams(queryString: string) {
  const queryParams = new URLSearchParams(queryString);
  return {
    cmId: queryParams.get('cmId'),
    claimId: queryParams.get('claimId'),
    printToPDF: queryParams.get('printToPdf') === 'true',
    isCorporate: queryParams.get('isCorporate') === 'true'
  };
}

export function AdvisorLoginForm({ onSubmit }: AdvisorLoginFormProps) {
  const [cmIdSearch, setCmIdSearch] = useState('');

  const ref = useRef<HTMLInputElement>(null);
  const {
    claimId,
    cmId: cmIdQueryParam,
    printToPDF,
    isCorporate
  } = extractQueryParams(window.location.search);

  if (claimId && cmIdQueryParam) {
    onSubmit({ cmId: cmIdQueryParam, claimId, printToPDF, isCorporate });
  }

  if (cmIdQueryParam) {
    onSubmit({ cmId: cmIdQueryParam, claimId, printToPDF, isCorporate });
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const input = ref.current!;
    const cmId = input.value.trim();

    if (cmId === '' || Number.isNaN(Number(cmId))) {
      input.focus();
      return;
    }

    onSubmit({ cmId, claimId, printToPDF, isCorporate });
  };

  return (
    <div style={{ maxWidth: 600, margin: '64px auto' }}>
      <Paper withShadow>
        <h1 className="dsm-heading3">Meld skade på vegne av kunde</h1>

        <form onSubmit={handleSubmit} noValidate style={{ marginTop: 16 }}>
          <Input
            id="advisor"
            name="advisor"
            ref={ref}
            label="Customer Master ID"
            defaultValue={cmIdQueryParam ?? undefined}
            value={cmIdSearch}
            onChange={e => {
              setCmIdSearch(e.target.value);
            }}
          />
          <Button type="submit" style={{ display: 'block', marginTop: 8 }}>
            Start
          </Button>
        </form>
      </Paper>
    </div>
  );
}
